import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { Avatar } from '@/components/atoms/Avatar/Avatar/Avatar';
import { EmptyValue } from '@/components/atoms/EmptyValue/EmptyValue';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import type { Simulation, SimulationSchedule } from '@/modules/simulations/types';
import { CHANNEL_LABEL_KEYS } from '@/modules/simulations/types';
import { CHANNEL_ICONS, describeCron } from '@/modules/simulations/utils';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import ActionsCell from './ActionsCell';

interface ColumnsProps {
  onRun: (ids: string[]) => void;
  onDelete: (id: string) => void;
  schedules: SimulationSchedule[] | undefined;
}

const buildScheduleMap = (schedules: SimulationSchedule[] | undefined) => {
  const map = new Map<string, SimulationSchedule[]>();
  const global: SimulationSchedule[] = [];

  if (!schedules) {
    return { map, global };
  }

  for (const schedule of schedules) {
    if (!schedule.simulation_ids) {
      global.push(schedule);
      continue;
    }
    for (const simId of schedule.simulation_ids) {
      const existing = map.get(simId);
      if (existing) {
        existing.push(schedule);
      } else {
        map.set(simId, [schedule]);
      }
    }
  }
  return { map, global };
};

export const columns = ({ onRun, onDelete, schedules }: ColumnsProps): ColumnDef<Simulation>[] => {
  const { map: scheduleMap, global: globalSchedules } = buildScheduleMap(schedules);

  return [
    {
      id: 'select',
      ...COLUMN_PRESETS.SM,
      meta: { preset: 'SM' },
      header: ({ table }) => {
        const getIsChecked = () => {
          if (table.getIsAllPageRowsSelected()) {
            return true;
          }
          if (table.getIsSomePageRowsSelected()) {
            return 'indeterminate';
          }
          return false;
        };

        return (
          <div className="flex h-8 w-8 items-center justify-center">
            <Checkbox
              checked={getIsChecked()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(Boolean(value))}
              aria-label={t('common.select_all_rows')}
            />
          </div>
        );
      },
      cell: ({ row }) => (
        <div
          className="flex h-8 w-8 items-center justify-center"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onCheckedChange={(checked) => row.toggleSelected(Boolean(checked))}
            aria-label={t('common.select_row')}
          />
        </div>
      ),
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      ...COLUMN_PRESETS.FLEX,
      meta: { preset: 'FLEX' },
      enableHiding: false,
      header: ({ column }) => (
        <SortableHeader column={column}>{t('simulations.name')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const { name, scenario } = row.original;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-medium">{name}</span>
              {scenario && (
                <span className="truncate text-xs text-muted-foreground">{scenario}</span>
              )}
            </div>
            <RowActionIndicator action="view" />
          </div>
        );
      },
    },
    {
      id: 'channel_type',
      accessorKey: 'channel_type',
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'simulations.channel' },
      header: ({ column }) => (
        <SortableHeader column={column}>{t('simulations.channel')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const channelType = row.original.channel_type;
        const Icon = CHANNEL_ICONS[channelType];
        return (
          <div className="flex items-center gap-2">
            <Icon size={16} className="shrink-0 text-muted-foreground" />
            <span className="truncate">{t(CHANNEL_LABEL_KEYS[channelType])}</span>
          </div>
        );
      },
    },
    {
      id: 'category',
      accessorKey: 'category',
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'simulations.category' },
      header: ({ column }) => (
        <SortableHeader column={column}>{t('simulations.category')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const { category } = row.original;
        if (!category) {
          return null;
        }
        return (
          <Badge type="secondary" flavour="neutral" rounded={false} className="max-w-full">
            <span className="truncate">{category}</span>
          </Badge>
        );
      },
    },
    {
      id: 'created',
      accessorKey: 'created',
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.created' },
      header: ({ column }) => (
        <SortableHeader column={column}>{t('common.created')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const { created, created_by } = row.original;
        if (!created) {
          return <EmptyValue />;
        }
        return (
          <div className="flex min-w-0 items-center gap-2">
            {created_by && <Avatar userId={created_by} size="small" hideStatus />}
            <TimestampTooltip timestamp={created}>{timeFromNow(created)}</TimestampTooltip>
          </div>
        );
      },
    },
    {
      id: 'updated',
      accessorKey: 'updated',
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'common.last_updated' },
      header: ({ column }) => (
        <SortableHeader column={column}>{t('common.last_updated')}</SortableHeader>
      ),
      cell: ({ row }) => {
        const { created, updated } = row.original;
        const isUnchanged = !updated || updated === created;
        if (isUnchanged) {
          return <EmptyValue />;
        }
        return <TimestampTooltip timestamp={updated}>{timeFromNow(updated)}</TimestampTooltip>;
      },
    },
    {
      id: 'schedules',
      ...COLUMN_PRESETS.MD,
      meta: { preset: 'MD', label: 'simulations.schedules' },
      header: () => t('simulations.schedules'),
      cell: ({ row }) => {
        const targeted = scheduleMap.get(row.original.simulation_id) ?? [];
        const simSchedules = [...globalSchedules, ...targeted];
        if (!simSchedules.length) {
          return <EmptyValue />;
        }
        return (
          <div
            role="presentation"
            className="flex flex-wrap gap-1"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {simSchedules.map((s) => (
              <InfoTooltip key={s.schedule_id} content={describeCron(s.cron_expression, t)}>
                <Badge type="secondary" flavour={s.is_active ? 'informational' : 'neutral'}>
                  {s.name}
                </Badge>
              </InfoTooltip>
            ))}
          </div>
        );
      },
    },
    {
      id: 'actions',
      ...COLUMN_PRESETS.SM,
      meta: { preset: 'SM' },
      enableHiding: false,
      cell: ({ row }) => (
        <ActionsCell simulation={row.original} onRun={(id) => onRun([id])} onDelete={onDelete} />
      ),
    },
  ];
};
